import Swal from 'sweetalert2';
import { Atendente, Chat } from '@/mocks/app/mockChatData';
import { branding } from '@/config/branding';

const brandColor = branding.colors.light.primary;

/**
 * Confirmação de abertura de um atendimento em espera (status `Criado`, sem
 * atendente vinculado).
 *
 * Só chega aqui quem NÃO tem `inbox.ver_previa_atendimentos_em_espera`: com a
 * permissão, o clique entra direto na prévia e a decisão de abrir passa para a
 * barra do composer (`chatIsOpenButAtendimentoIsNotOpen`).
 *
 * Segue o padrão visual de `FinalizarAtendimento` e do seletor de integração
 * Meta (`escolherEmCards`): ícone circular, `buttonsStyling: false` e botões
 * `rounded-pill` — em vez do ícone `warning` padrão do SweetAlert.
 */
export async function PromptToAbrirAtendimento(chat: Chat): Promise<boolean> {
    const nome = chat.name?.trim();

    const { isConfirmed } = await Swal.fire({
        title: '',
        html: `
            <div class="d-flex flex-column align-items-center pt-2">
                <div class="mb-3 d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle"
                    style="width: 72px; height: 72px;">
                    <i class="ri-chat-check-line text-primary" style="font-size: 32px;"></i>
                </div>
                <h4 class="fw-bold mb-2 text-dark">Iniciar Atendimento?</h4>
                <p class="text-muted mb-0 px-2" style="font-size: 0.95rem; line-height: 1.5;">
                    ${nome
                        ? `O atendimento com <strong class="text-dark">${escapeHtml(nome)}</strong> será aberto e vinculado a você.`
                        : 'O atendimento será aberto e vinculado a você.'}
                </p>
            </div>
        `,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: '<i class="ri-play-fill me-1"></i> Sim, abrir atendimento',
        cancelButtonText: 'Cancelar',
        buttonsStyling: false,
        customClass: {
            popup: 'rounded-4 border-0 shadow-lg',
            confirmButton: 'btn btn-primary rounded-pill px-4 fw-semibold mx-1 shadow-sm',
            cancelButton: 'btn btn-light rounded-pill px-4 fw-semibold mx-1 border text-muted',
            actions: 'w-100 pb-4 px-2 d-flex justify-content-center flex-wrap-reverse gap-2',
        },
        didOpen: (popup) => {
            const btnConfirm = popup.querySelector('.swal2-confirm') as HTMLElement | null;
            if (btnConfirm) {
                btnConfirm.style.backgroundColor = brandColor;
                btnConfirm.style.borderColor = brandColor;
            }
        },
    });

    return isConfirmed;
}

/**
 * Confirmação de abertura de um atendimento em espera que JÁ está vinculado a
 * outro atendente — o caso típico é o "transferir e finalizar", que cria o
 * atendimento novo em `Criado` no nome do receptor, visível também ao root.
 *
 * Abrir aqui é tomar o atendimento de alguém: o servidor registra uma
 * transferência para quem abriu e avisa o atendente anterior.
 */
export async function PromptToAssumirAtendimento(chat: Chat, atendenteAtual: Atendente): Promise<boolean> {
    const nome = chat.name?.trim();
    const contato = nome ? ` com <strong class="text-dark">${escapeHtml(nome)}</strong>` : '';

    return await confirmarAssuncao(
        'Assumir atendimento?',
        `O atendimento${contato} está com <strong class="text-dark">${escapeHtml(atendenteAtual.name)}</strong>. Ao abrir, ele será transferido para você.`,
        '<i class="ri-user-shared-line me-1"></i> Sim, transferir para mim',
    );
}

/** Versão em lote: uma única confirmação para todos os que já têm atendente. */
export async function PromptToAssumirAtendimentosEmLote(quantidade: number): Promise<boolean> {
    return await confirmarAssuncao(
        'Assumir atendimentos?',
        quantidade === 1
            ? '<strong class="text-dark">1</strong> dos atendimentos selecionados já está com outro atendente. Ao abrir, ele será transferido para você.'
            : `<strong class="text-dark">${quantidade}</strong> dos atendimentos selecionados já estão com outros atendentes. Ao abrir, eles serão transferidos para você.`,
        '<i class="ri-user-shared-line me-1"></i> Sim, transferir para mim',
    );
}

async function confirmarAssuncao(titulo: string, mensagemHtml: string, textoConfirmar: string): Promise<boolean> {
    const { isConfirmed } = await Swal.fire({
        title: '',
        html: `
            <div class="d-flex flex-column align-items-center pt-2">
                <div class="mb-3 d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle"
                    style="width: 72px; height: 72px;">
                    <i class="ri-user-shared-line text-warning" style="font-size: 32px;"></i>
                </div>
                <h4 class="fw-bold mb-2 text-dark">${titulo}</h4>
                <p class="text-muted mb-0 px-2" style="font-size: 0.95rem; line-height: 1.5;">${mensagemHtml}</p>
            </div>
        `,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: textoConfirmar,
        cancelButtonText: 'Cancelar',
        buttonsStyling: false,
        customClass: {
            popup: 'rounded-4 border-0 shadow-lg',
            confirmButton: 'btn btn-primary rounded-pill px-4 fw-semibold mx-1 shadow-sm',
            cancelButton: 'btn btn-light rounded-pill px-4 fw-semibold mx-1 border text-muted',
            actions: 'w-100 pb-4 px-2 d-flex justify-content-center flex-wrap-reverse gap-2',
        },
        didOpen: (popup) => {
            const btnConfirm = popup.querySelector('.swal2-confirm') as HTMLElement | null;
            if (btnConfirm) {
                btnConfirm.style.backgroundColor = brandColor;
                btnConfirm.style.borderColor = brandColor;
            }
        },
    });

    return isConfirmed;
}

/** O nome do contato é conteúdo de terceiro; nunca entra cru no `html` do Swal. */
function escapeHtml(valor: string): string {
    return valor
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
